import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { message } from "antd";
import { useTheme } from "../../ThemeContext";
import { completeOnboarding } from "./onboarding-services";
import OnboardingStepper from "./components/OnboardingStepper";
import CustomerDetailsStep from "./steps/CustomerDetailsStep";
import AdminDetailsStep from "./steps/AdminDetailsStep";
import FacilityDetailsStep from "./steps/FacilityDetailsStep";
import ProviderDetailsStep from "./steps/ProviderDetailsStep";
import DocumentSigning from "../documentSigning/DocumentSigning";
import "./Onboarding.css";

const Onboarding = () => {
  const { themeMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    console.log(themeMode, "themeMode");
    if (themeMode === 'dark') {
      toggleTheme();
    }
  }, []);

  // Determine current step based on route
  const getStepFromPath = (path) => {
    if (path.includes('document-signing')) return 0;
    if (path.includes('customer-details')) return 1;
    if (path.includes('admin-details')) return 2;
    if (path.includes('facility-details')) return 3;
    if (path.includes('provider-details')) return 4;
    return 0; // Default to document signing
  };

  const [currentStep, setCurrentStep] = useState(getStepFromPath(location.pathname));
  const [isLoading, setIsLoading] = useState(false);
  const [completingOnboarding, setCompletingOnboarding] = useState(false);
  const [documentSigningCompleted, setDocumentSigningCompleted] = useState(false);

  // Update step when route changes
  useEffect(() => {
    const newStep = getStepFromPath(location.pathname);
    setCurrentStep(newStep);

    // If we're at the root onboarding path, redirect to document signing
    if (location.pathname === '/onboarding' || location.pathname === '/onboarding/') {
      navigate('/onboarding/document-signing');
    }
  }, [location.pathname, navigate]);

  const steps = [
    {
      id: 1,
      title: "Document Signing",
      subtitle: "Sign required legal documents to proceed.",
      path: "/onboarding/document-signing"
    },
    {
      id: 2,
      title: "Customer Details",
      subtitle: "Enter all required customer information.",
      path: "/onboarding/customer-details"
    },
    {
      id: 3,
      title: "Admin Details",
      subtitle: "Provide details of the account administrator.",
      path: "/onboarding/admin-details"
    },
    {
      id: 4,
      title: "Facility Information",
      subtitle: "Provide details of the account administrator.",
      path: "/onboarding/facility-details"
    },
    {
      id: 5,
      title: "Provider Information",
      subtitle: "Enter the provider's details.",
      path: "/onboarding/provider-details"
    }
  ];

  const handleNext = () => {
    console.log('🚀 handleNext called - currentStep:', currentStep, 'documentSigningCompleted:', documentSigningCompleted);

    // If we're on step 0 (document signing) and documentSigningCompleted is false,
    // but onNext was called, it means the documents were just completed
    if (currentStep === 0 && !documentSigningCompleted) {
      console.log('📋 Document signing just completed, setting documentSigningCompleted to true');
      setDocumentSigningCompleted(true);
    }

    if (currentStep < steps.length - 1) {
      const nextStep = currentStep + 1;
      console.log('🔄 Navigating to next step:', nextStep, 'path:', steps[nextStep].path);
      navigate(steps[nextStep].path);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      const prevStep = currentStep - 1;
      navigate(steps[prevStep].path);
    }
  };

  const handleFinishOnboarding = async () => {
    setCompletingOnboarding(true);
    try {
      message.loading("Creating your trial account...", 0);

      await completeOnboarding();

      message.destroy();
      message.success("Onboarding completed successfully!");

      // Redirect to billing page
      setTimeout(() => {
        navigate("/billing");
      }, 2000);

    } catch (error) {
      message.destroy();
      console.error("Error completing onboarding:", error);
      message.error("Failed to complete onboarding. Please try again.");
    } finally {
      setCompletingOnboarding(false);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 0:
        return (
          <DocumentSigning
            onNext={handleNext}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
            setDocumentSigningCompleted={setDocumentSigningCompleted}
          />
        );
      case 1:
        return (
          <CustomerDetailsStep
            onPrevious={handlePrevious}
            onNext={handleNext}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
          />
        );
      case 2:
        return (
          <AdminDetailsStep
            onNext={handleNext}
            onPrevious={handlePrevious}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
          />
        );
      case 3:
        return (
          <FacilityDetailsStep
            onNext={handleNext}
            onPrevious={handlePrevious}
            isLoading={isLoading}
            setIsLoading={setIsLoading}
          />
        );
      case 4:
        return (
          <ProviderDetailsStep
            onFinish={handleFinishOnboarding}
            onPrevious={handlePrevious}
            isLoading={isLoading || completingOnboarding}
            setIsLoading={setIsLoading}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={`onboarding-container ${themeMode}`}>
      <div className="onboarding-content">
        <OnboardingStepper
          steps={steps}
          currentStep={currentStep}
          themeMode={themeMode}
          documentSigningCompleted={documentSigningCompleted}
        />

        <div className="onboarding-step-content">
          {renderStep()}
        </div>
      </div>
    </div>
  );
};

export default Onboarding; 